// API adapter for the notification inbox. Thin functions that call the
// authenticated apiCall and shape typed responses. All logic that the bell,
// dropdown, and screen need lives in the hooks that wrap these.
//
// Contract (aligned with Handoff 3):
//   GET  /api/notifications?before=&type=&limit=  -> NotificationPage
//   GET  /api/notifications/unread-count          -> { count }
//   POST /api/notifications/:id/read
//   POST /api/notifications/read-all

import type {
  NotificationItem,
  NotificationPage,
  NotificationPreferences,
  NotificationPreferencesPatch,
  NotificationType,
} from "./model.js";
import { isMockNotificationsEnabled } from "./mockConfig.js";
import {
  getMockPreferences,
  getMockUnreadCount,
  listMockNotifications,
  markAllMockRead,
  markMockRead,
  updateMockPreferences,
} from "./mockStore.js";

type ApiCall = (url: string, options?: RequestInit) => Promise<Response>;

export interface GetNotificationsParams {
  before?: string | null;
  type?: NotificationType | null;
  limit?: number;
}

function buildQuery({ before, type, limit }: GetNotificationsParams): string {
  const params = new URLSearchParams();
  if (before) params.set("before", before);
  if (type) params.set("type", type);
  if (limit) params.set("limit", String(limit));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export async function getNotifications(
  apiCall: ApiCall,
  params: GetNotificationsParams = {},
): Promise<NotificationPage> {
  if (isMockNotificationsEnabled()) return listMockNotifications(params);
  const res = await apiCall(`/api/notifications${buildQuery(params)}`);
  if (!res.ok) throw new Error(`Failed to load notifications (${res.status})`);
  const data = await res.json();
  return {
    items: Array.isArray(data?.items) ? (data.items as NotificationItem[]) : [],
    nextBefore: data?.nextBefore ?? null,
  };
}

export async function getUnreadCount(apiCall: ApiCall): Promise<number> {
  if (isMockNotificationsEnabled()) return getMockUnreadCount();
  const res = await apiCall("/api/notifications/unread-count");
  if (!res.ok) throw new Error(`Failed to load unread count (${res.status})`);
  const data = await res.json();
  return typeof data?.count === "number" ? data.count : 0;
}

export async function markNotificationRead(
  apiCall: ApiCall,
  id: string,
): Promise<void> {
  if (isMockNotificationsEnabled()) {
    markMockRead(id);
    return;
  }
  const res = await apiCall(`/api/notifications/${id}/read`, { method: "POST" });
  if (!res.ok) throw new Error(`Failed to mark read (${res.status})`);
}

export async function markAllNotificationsRead(
  apiCall: ApiCall,
): Promise<void> {
  if (isMockNotificationsEnabled()) {
    markAllMockRead();
    return;
  }
  const res = await apiCall("/api/notifications/read-all", { method: "POST" });
  if (!res.ok) throw new Error(`Failed to mark all read (${res.status})`);
}

export async function getNotificationPreferences(
  apiCall: ApiCall,
): Promise<NotificationPreferences> {
  if (isMockNotificationsEnabled()) return getMockPreferences();
  const res = await apiCall("/api/notifications/preferences");
  if (!res.ok) throw new Error(`Failed to load preferences (${res.status})`);
  const data = await res.json();
  return {
    notificationPush: data?.notificationPush ?? true,
    types: (data?.types ?? {}) as NotificationPreferences["types"],
  };
}

export async function updateNotificationPreferences(
  apiCall: ApiCall,
  patch: NotificationPreferencesPatch,
): Promise<void> {
  if (isMockNotificationsEnabled()) {
    updateMockPreferences(patch);
    return;
  }
  const res = await apiCall("/api/notifications/preferences", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`Failed to update preferences (${res.status})`);
}
